
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Injectable } from "@angular/core";

/**
 * Central browser-storage access point (aligned with the VNCmail /
 * VNCcontacts pattern). Static methods keep call-site conversion mechanical
 * and work outside DI contexts; the injectable instance methods exist for
 * components that prefer constructor/inject() usage.
 */
@Injectable({ providedIn: "root" })
export class StorageService {
  private static getStorage(type: "localStorage" | "sessionStorage"): Storage | null {
    if (typeof window === "undefined") {
      return null;
    }

    try {
      return window[type];
    } catch {
      return null;
    }
  }

  static getLocalItem(key: string): string | null {
    return StorageService.getStorage("localStorage")?.getItem(key) ?? null;
  }

  static setLocalItem(key: string, value: string): void {
    StorageService.getStorage("localStorage")?.setItem(key, value);
  }

  static removeLocalItem(key: string): void {
    StorageService.getStorage("localStorage")?.removeItem(key);
  }

  static clearLocal(): void {
    StorageService.getStorage("localStorage")?.clear();
  }

  static getSessionItem(key: string): string | null {
    return StorageService.getStorage("sessionStorage")?.getItem(key) ?? null;
  }

  static setSessionItem(key: string, value: string): void {
    StorageService.getStorage("sessionStorage")?.setItem(key, value);
  }

  static removeSessionItem(key: string): void {
    StorageService.getStorage("sessionStorage")?.removeItem(key);
  }

  static clearSession(): void {
    StorageService.getStorage("sessionStorage")?.clear();
  }

  getItem(key: string): string | null {
    return StorageService.getLocalItem(key);
  }

  setItem(key: string, value: string): void {
    StorageService.setLocalItem(key, value);
  }

  removeItem(key: string): void {
    StorageService.removeLocalItem(key);
  }

  clear(): void {
    StorageService.clearLocal();
  }
}
